// =============================================================================
// Phase 5: Time Parser
// Parses time in various formats: 24-hour, 12-hour (am/pm), European (h notation)
// Also handles concatenated formats (1915, 240am) and granularity-based normalization
// =============================================================================

import { validateTime } from "./utils/calendar";
import type { TimeGranularity } from "../../types";

export interface TimeResult {
  isValid: boolean;
  hour?: number;
  minute?: number;
  second?: number;
  millisecond?: number;
  error?: string;
}

export interface NormalizedTime {
  hour: number;
  minute: number;
  second: number;
  formatted: string;
}

/**
 * Parse time string into components
 * Supports: 24-hour (14:30), 12-hour (2pm, 2:30pm), European (14h30),
 *           concatenated (1915, 915), concatenated 12-hour (240am, 1130pm)
 */
export function parseTime(input: string): TimeResult {
  const trimmed = input.trim();

  // Handle empty input
  if (!trimmed) {
    return { isValid: false };
  }

  // Try European format first (14h, 14h30)
  const europeanResult = parseEuropeanFormat(trimmed);
  if (europeanResult) return europeanResult;

  // Try concatenated 12-hour format (240am, 240pm, 1130pm) - before regular 12-hour
  const concatenated12HourResult = parseConcatenated12HourFormat(trimmed);
  if (concatenated12HourResult) return concatenated12HourResult;

  // Try 12-hour format (2pm, 2:30pm, 2 pm, 2:30 pm, p.m. variations)
  const twelveHourResult = parse12HourFormat(trimmed);
  if (twelveHourResult) return twelveHourResult;

  // Try 24-hour format (14:30, 14:30:45, 14:30:45.123)
  const twentyFourHourResult = parse24HourFormat(trimmed);
  if (twentyFourHourResult) return twentyFourHourResult;

  // Try concatenated 24-hour format (1915, 915) - after colon format to avoid conflicts
  const concatenatedResult = parseConcatenatedFormat(trimmed);
  if (concatenatedResult) return concatenatedResult;

  // Try hour only (14, 9, 0, 23)
  const hourOnlyResult = parseHourOnly(trimmed);
  if (hourOnlyResult) return hourOnlyResult;

  // Invalid format
  throw new Error(`Invalid time format: ${input}`);
}

/**
 * Parse European format (14h, 14h30)
 */
export function parseEuropeanFormat(input: string): TimeResult | null {
  // Match: HH?h or HH?hMM?
  const match = input.match(/^(\d{1,2})h(\d{1,2})?$/i);
  if (!match) return null;

  const hour = parseInt(match[1], 10);
  const minute = match[2] ? parseInt(match[2], 10) : 0;

  validateTime(hour, minute);

  return {
    isValid: true,
    hour,
    minute,
  };
}

/**
 * Parse 12-hour format (2pm, 2:30pm, 2 pm, 2:30 pm, 2:30:45pm, p.m. variations)
 */
export function parse12HourFormat(input: string): TimeResult | null {
  // Normalize p.m./a.m. to pm/am
  const normalized = input.replace(/\s*(a|p)\.m\./gi, (_, letter) => letter.toLowerCase() + "m");

  // Match: H(:MM(:SS)?)? ?am/pm
  const match = normalized.match(/^(\d{1,2})(?::(\d{1,2})(?::(\d{1,2}))?)?\s*(am|pm)$/i);
  if (!match) return null;

  let hour = parseInt(match[1], 10);
  const minute = match[2] ? parseInt(match[2], 10) : 0;
  const second = match[3] ? parseInt(match[3], 10) : undefined;
  const isPM = match[4].toLowerCase() === "pm";

  // Validate 12-hour format (1-12 only)
  if (hour < 1 || hour > 12) {
    throw new Error(`Invalid 12-hour format: ${hour}`);
  }

  // Validate minute and second ranges
  if (minute < 0 || minute > 59) {
    throw new Error(`Invalid minute: ${minute}`);
  }
  if (second !== undefined && (second < 0 || second > 59)) {
    throw new Error(`Invalid second: ${second}`);
  }

  // Convert to 24-hour
  if (isPM) {
    hour = hour === 12 ? 12 : hour + 12;
  } else {
    hour = hour === 12 ? 0 : hour;
  }

  const result: TimeResult = {
    isValid: true,
    hour,
    minute,
  };

  if (second !== undefined) {
    result.second = second;
  }

  return result;
}

/**
 * Parse 24-hour format (14:30, 14:30:45, 14:30:45.123)
 */
export function parse24HourFormat(input: string): TimeResult | null {
  // Check for incomplete formats first
  if (input.startsWith(":") || input.endsWith(":")) {
    throw new Error(`Invalid time format: ${input}`);
  }

  // Match: HH:MM(:SS(.mmm)?)?
  const match = input.match(/^(\d{1,3}):(\d{1,3})(?::(\d{1,3})(?:\.(\d{1,3}))?)?$/);
  if (!match) return null;

  const hour = parseInt(match[1], 10);
  const minute = parseInt(match[2], 10);
  const second = match[3] ? parseInt(match[3], 10) : undefined;
  const millisecond = match[4] ? parseInt(match[4].padEnd(3, "0").slice(0, 3), 10) : undefined;

  validateTime(hour, minute, second);

  const result: TimeResult = {
    isValid: true,
    hour,
    minute,
  };

  if (second !== undefined) {
    result.second = second;
  }

  if (millisecond !== undefined) {
    result.millisecond = millisecond;
  }

  return result;
}

/**
 * Parse hour only (14, 9, 0, 23)
 */
export function parseHourOnly(input: string): TimeResult | null {
  // Must be exactly 1-2 digits
  if (!/^\d{1,2}$/.test(input)) return null;

  const hour = parseInt(input, 10);

  // Validate hour range
  if (hour < 0 || hour > 23) {
    throw new Error(`Invalid hour: ${hour}`);
  }

  return {
    isValid: true,
    hour,
    minute: 0,
  };
}

/**
 * Parse concatenated format (1915 → 19:15, 915 → 09:15, 152530 → 15:25:30, 91530 → 09:15:30)
 */
export function parseConcatenatedFormat(input: string): TimeResult | null {
  // Must be exactly 3-6 digits (no am/pm suffix)
  // 3 digits: HMM (915 → 09:15)
  // 4 digits: HHMM (1525 → 15:25)
  // 5 digits: HMMSS (91530 → 09:15:30)
  // 6 digits: HHMMSS (152530 → 15:25:30)
  const match = input.match(/^(\d{3,6})$/);
  if (!match) return null;

  const digits = match[1];
  let hour: number;
  let minute: number;
  let second: number | undefined;

  if (digits.length === 6) {
    // HHMMSS format: 152530 → 15:25:30
    hour = parseInt(digits.slice(0, 2), 10);
    minute = parseInt(digits.slice(2, 4), 10);
    second = parseInt(digits.slice(4, 6), 10);
  } else if (digits.length === 5) {
    // HMMSS format: 91530 → 09:15:30
    hour = parseInt(digits.slice(0, 1), 10);
    minute = parseInt(digits.slice(1, 3), 10);
    second = parseInt(digits.slice(3, 5), 10);
  } else if (digits.length === 4) {
    // HHMM format: 1915 → 19:15
    hour = parseInt(digits.slice(0, 2), 10);
    minute = parseInt(digits.slice(2), 10);
  } else {
    // HMM format: 915 → 09:15
    hour = parseInt(digits.slice(0, 1), 10);
    minute = parseInt(digits.slice(1), 10);
  }

  validateTime(hour, minute, second);

  const result: TimeResult = {
    isValid: true,
    hour,
    minute,
  };

  if (second !== undefined) {
    result.second = second;
  }

  return result;
}

/**
 * Parse concatenated 12-hour format (240am → 02:40, 1130pm → 23:30, 24030am → 02:40:30, 113045pm → 23:30:45)
 */
export function parseConcatenated12HourFormat(input: string): TimeResult | null {
  // Match: 3-6 digits followed by am/pm
  // 3 digits: HMM (240am → 02:40)
  // 4 digits: HHMM (1130pm → 23:30)
  // 5 digits: HMMSS (24030am → 02:40:30)
  // 6 digits: HHMMSS (113045pm → 23:30:45)
  const match = input.match(/^(\d{3,6})(am|pm)$/i);
  if (!match) return null;

  const digits = match[1];
  const isPM = match[2].toLowerCase() === "pm";
  let hour: number;
  let minute: number;
  let second: number | undefined;

  if (digits.length === 6) {
    // HHMMSS format: 113045pm → 11:30:45 PM
    hour = parseInt(digits.slice(0, 2), 10);
    minute = parseInt(digits.slice(2, 4), 10);
    second = parseInt(digits.slice(4, 6), 10);
  } else if (digits.length === 5) {
    // HMMSS format: 24030am → 2:40:30 AM
    hour = parseInt(digits.slice(0, 1), 10);
    minute = parseInt(digits.slice(1, 3), 10);
    second = parseInt(digits.slice(3, 5), 10);
  } else if (digits.length === 4) {
    // HHMM format: 1130pm → 11:30 PM
    hour = parseInt(digits.slice(0, 2), 10);
    minute = parseInt(digits.slice(2), 10);
  } else {
    // HMM format: 240am → 2:40 AM
    hour = parseInt(digits.slice(0, 1), 10);
    minute = parseInt(digits.slice(1), 10);
  }

  // Validate 12-hour range (1-12)
  if (hour < 1 || hour > 12) {
    throw new Error(`Invalid 12-hour format: ${hour}`);
  }

  // Validate minute range
  if (minute < 0 || minute > 59) {
    throw new Error(`Invalid minute: ${minute}`);
  }

  // Validate second range
  if (second !== undefined && (second < 0 || second > 59)) {
    throw new Error(`Invalid second: ${second}`);
  }

  // Convert to 24-hour
  if (isPM) {
    hour = hour === 12 ? 12 : hour + 12;
  } else {
    hour = hour === 12 ? 0 : hour;
  }

  const result: TimeResult = {
    isValid: true,
    hour,
    minute,
  };

  if (second !== undefined) {
    result.second = second;
  }

  return result;
}

/**
 * Normalize time based on granularity and format to string
 * - hour: truncates minutes and seconds, formats as "HH:00"
 * - minute: truncates seconds, formats as "HH:mm"
 * - second: keeps all precision, formats as "HH:mm:ss"
 */
export function normalizeTime(
  time: TimeResult,
  granularity: TimeGranularity
): NormalizedTime {
  const hour = time.hour ?? 0;
  let minute = time.minute ?? 0;
  let second = time.second ?? 0;

  // Apply granularity truncation
  switch (granularity) {
    case "hour":
      minute = 0;
      second = 0;
      break;
    case "minute":
      second = 0;
      break;
    case "second":
      // Keep all precision
      break;
  }

  // Format string based on granularity
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute).padStart(2, "0");
  const ss = String(second).padStart(2, "0");

  let formatted: string;
  switch (granularity) {
    case "hour":
      formatted = `${hh}:00`;
      break;
    case "second":
      formatted = `${hh}:${mm}:${ss}`;
      break;
    case "minute":
    case "date":
    default:
      formatted = `${hh}:${mm}`;
      break;
  }

  return { hour, minute, second, formatted };
}

// =============================================================================
// Time Input Parsing (for CalendarBox and TimeInput components)
// =============================================================================

export interface ParseTimeInputResult {
  value: string;
  isValid: boolean;
}

/**
 * Parse various time input formats and normalize to HH:mm or HH:mm:ss
 * Returns the parsed value and whether it's valid.
 * Unlike parseTime(), this function does NOT throw - it returns isValid: false for invalid input.
 *
 * Supported formats:
 * - 24-hour: 14:30, 14:30:45, 9:05
 * - 12-hour: 2pm, 2:30pm, 2 pm, 2:30 pm, 2:30:45pm, p.m. variations
 * - European: 14h, 14h30
 * - Concatenated: 1915, 915, 152530
 * - Concatenated 12-hour: 240am, 1130pm, 24030am
 * - Hour only: 14, 9
 */
export function parseTimeInput(
  input: string,
  granularity: TimeGranularity
): ParseTimeInputResult {
  if (!input || input.trim() === "") {
    return { value: "", isValid: true };
  }

  const trimmed = input.trim().toLowerCase();

  // Check for relative expressions (reject them - return raw value)
  if (trimmed.includes("now") || (trimmed.includes("-") && !trimmed.match(/^\d/))) {
    return { value: input, isValid: false };
  }

  try {
    // Use the existing parseTime function which handles all formats
    const result = parseTime(trimmed);

    if (result.isValid && result.hour !== undefined) {
      const normalized = normalizeTime(result, granularity);
      return { value: normalized.formatted, isValid: true };
    }

    return { value: input, isValid: false };
  } catch {
    // parseTime throws on invalid input - return the raw value as invalid
    return { value: input, isValid: false };
  }
}

/**
 * Check if a string is in valid normalized time format (HH:mm or HH:mm:ss)
 */
export function isValidNormalizedTime(value: string): boolean {
  if (!value) return false;

  // Check HH:mm format
  const minuteMatch = value.match(/^(\d{2}):(\d{2})$/);
  if (minuteMatch) {
    const hour = parseInt(minuteMatch[1], 10);
    const minute = parseInt(minuteMatch[2], 10);
    return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;
  }

  // Check HH:mm:ss format
  const secondMatch = value.match(/^(\d{2}):(\d{2}):(\d{2})$/);
  if (secondMatch) {
    const hour = parseInt(secondMatch[1], 10);
    const minute = parseInt(secondMatch[2], 10);
    const second = parseInt(secondMatch[3], 10);
    return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59 && second >= 0 && second <= 59;
  }

  return false;
}
